import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { DateTime } from 'luxon';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useIsMobile } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { getCustomDateBounds } from '@/lib/campaign-schedule';
import { mobileLayer } from '@/lib/mobile-layer';
import { timezoneCity, timezoneLabel } from '@/lib/timezone-label';
import { cn, getLuxonLocale } from '@/lib/utils';

const DATE_FORMAT = 'yyyy-MM-dd';

export interface CustomSchedule {
  date: string; // yyyy-MM-dd
  time: string; // HH:mm
  timezone: string;
}

export interface CustomScheduleSheetProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  initial: { date: string; time: string } | null;
  timezone: string;
  now: DateTime;
  onApply(value: CustomSchedule): void;
}

/** yyyy-MM-dd -> local midnight `Date` (what react-day-picker works with). */
function toPickerDate(date: string): Date | undefined {
  const day = DateTime.fromFormat(date, DATE_FORMAT);
  return day.isValid ? day.toJSDate() : undefined;
}

/**
 * "Outra data" (PUSH-05 AC 4): date (today .. +2 months in the chosen zone),
 * native time input and timezone search, in a `Drawer` below 768px and a
 * `Dialog` from 768px up (AD-016). "Aplicar" needs both date and time.
 */
export function CustomScheduleSheet(props: CustomScheduleSheetProps) {
  const { LL } = useI18nContext();
  const isMobile = useIsMobile();
  const { open, onOpenChange } = props;
  const title = LL.campaignComposer.schedule.sheet.title();
  const description = LL.campaignComposer.schedule.sheet.description();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[92dvh]">
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          {open && <ScheduleForm {...props} variant="mobile" />}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {open && <ScheduleForm {...props} variant="desktop" />}
      </DialogContent>
    </Dialog>
  );
}

// Brazil's four official zones, offered first by the name people use.
const BRAZIL_ZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Rio_Branco',
  'America/Noronha',
] as const;

// "Horário de Brasília · Sao Paulo": the name people use, and the city that
// tells apart zones sharing it. Naming a zone costs ~1ms (Intl), so only
// Brazil's and the chosen one are named; the rest read "Tokyo · Asia".
function describeTimezone(tz: string, locale: Locales): string {
  return `${timezoneLabel(tz, getLuxonLocale(locale))} · ${timezoneCity(tz)}`;
}

function describeByCity(tz: string): string {
  const region = tz.includes('/') ? tz.split('/')[0] : '';
  return region ? `${timezoneCity(tz)} · ${region}` : tz;
}

function ScheduleForm({
  initial,
  timezone: initialTimezone,
  now,
  onApply,
  onOpenChange,
  variant,
}: CustomScheduleSheetProps & { variant: 'mobile' | 'desktop' }) {
  const { LL, locale } = useI18nContext();
  const [date, setDate] = useState(initial?.date ?? '');
  const [time, setTime] = useState(initial?.time ?? '');
  const [timezone, setTimezone] = useState(initialTimezone);
  const [pickingTimezone, setPickingTimezone] = useState(false);
  const zoneGroups = useMemo(() => {
    if (!pickingTimezone) return null;
    const brazil = new Set<string>(BRAZIL_ZONES);
    return {
      brazil: BRAZIL_ZONES.map((tz) => ({
        tz,
        text: describeTimezone(tz, locale),
      })),
      others: Intl.supportedValuesOf('timeZone')
        .filter((tz) => !brazil.has(tz))
        .map((tz) => ({ tz, text: describeByCity(tz) })),
    };
  }, [pickingTimezone, locale]);
  const zoneItem = ({ tz, text }: { tz: string; text: string }) => (
    <CommandItem
      key={tz}
      value={`${text} ${tz}`}
      onSelect={() => {
        setTimezone(tz);
        setPickingTimezone(false);
      }}
    >
      <Check
        className={cn(
          'mr-2 size-4',
          tz === timezone ? 'opacity-100' : 'opacity-0'
        )}
      />
      {text}
    </CommandItem>
  );

  const bounds = getCustomDateBounds(now, timezone);
  const fromDate = toPickerDate(bounds.min);
  const toDate = toPickerDate(bounds.max);
  const selected = toPickerDate(date);
  const canApply = date !== '' && time !== '';

  const apply = (
    <Button
      type="button"
      className={mobileLayer.touch}
      disabled={!canApply}
      onClick={() => onApply({ date, time, timezone })}
    >
      {LL.campaignComposer.schedule.sheet.apply()}
    </Button>
  );
  const cancel = (
    <Button
      type="button"
      variant="outline"
      className={mobileLayer.touch}
      onClick={() => onOpenChange(false)}
    >
      {LL.campaignComposer.schedule.sheet.cancel()}
    </Button>
  );

  const fields = (
    <div className="flex flex-col gap-4">
      <Calendar
        mode="single"
        showOutsideDays={false}
        selected={selected}
        defaultMonth={selected ?? fromDate}
        fromDate={fromDate}
        toDate={toDate}
        onSelect={(day) =>
          setDate(day ? DateTime.fromJSDate(day).toFormat(DATE_FORMAT) : '')
        }
        className="mx-auto rounded-md border"
      />
      <div className="space-y-2">
        <Label htmlFor="custom-schedule-time">
          {LL.campaignComposer.schedule.sheet.time()}
        </Label>
        <Input
          id="custom-schedule-time"
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className={mobileLayer.input}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="custom-schedule-timezone">
          {LL.campaignComposer.schedule.sheet.timezone()}
        </Label>
        <Button
          id="custom-schedule-timezone"
          type="button"
          variant="outline"
          aria-expanded={pickingTimezone}
          className={cn('w-full justify-between', mobileLayer.touch)}
          onClick={() => setPickingTimezone((v) => !v)}
        >
          <span className="truncate">{describeTimezone(timezone, locale)}</span>
        </Button>
        {pickingTimezone && (
          <Command className="rounded-md border">
            <CommandInput
              placeholder={LL.campaignComposer.schedule.sheet.timezoneSearch()}
              aria-label={LL.campaignComposer.schedule.sheet.timezoneSearch()}
              className={mobileLayer.input}
            />
            <CommandList className="max-h-48">
              <CommandEmpty>
                {LL.campaignComposer.schedule.sheet.timezoneEmpty()}
              </CommandEmpty>
              {zoneGroups && (
                <>
                  <CommandGroup
                    heading={LL.campaignComposer.schedule.sheet.brazilZones()}
                  >
                    {zoneGroups.brazil.map(zoneItem)}
                  </CommandGroup>
                  <CommandGroup
                    heading={LL.campaignComposer.schedule.sheet.otherZones()}
                  >
                    {zoneGroups.others.map(zoneItem)}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        )}
      </div>
    </div>
  );

  if (variant === 'mobile') {
    return (
      <>
        <div className="overflow-y-auto px-4">{fields}</div>
        <DrawerFooter>
          {apply}
          {cancel}
        </DrawerFooter>
      </>
    );
  }

  return (
    <>
      {fields}
      <DialogFooter>
        {cancel}
        {apply}
      </DialogFooter>
    </>
  );
}
